// Horizontal Type Mask Tool -- creates a selection in the shape of text.
// Photoshop: T -> Horizontal Type Mask Tool.
// Instead of creating a type layer, it creates a selection from the text outline.

import { screenToDoc } from "../render/Viewport";
import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface TypeMaskToolCallbacks {
  requestText: (x: number, y: number) => Promise<string | null>;
}

/**
 * Injected by main.ts so HorizontalTypeMaskTool can rasterize text without DOM.
 * Returns an alpha mask (Uint8Array, length = docW * docH, values 0 or 255).
 */
export type TextRasterizerFn = (
  docW: number,
  docH: number,
  text: string,
  x: number,
  y: number,
  fontCss: string,
  vertical: boolean,
  charSpacing: number,
) => Uint8Array;

/**
 * No-op fallback -- returns an all-zero (empty) mask.
 * Used in headless/test environments where no Canvas2D is available.
 */
export function noopTextRasterizer(docW: number, docH: number): Uint8Array {
  return new Uint8Array(docW * docH);
}

export class HorizontalTypeMaskTool implements Tool {
  readonly id = "type-mask-horizontal";
  readonly shortcut = "T";
  private callbacks: TypeMaskToolCallbacks | null = null;

  fontFamily = "Arial";
  fontSize = 24;
  fontWeight = 400;
  fontStyle = "normal";

  /** Set by main.ts to rasterize text via Canvas2D. */
  textRasterizer: TextRasterizerFn | null = null;

  setCallbacks(callbacks: TypeMaskToolCallbacks): void {
    this.callbacks = callbacks;
  }

  async onPointerDown(ctx: ToolContext, s: PointerSample): Promise<void> {
    if (!ctx.doc || !this.callbacks) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const text = await this.callbacks.requestText(p.x, p.y);
    if (!text || !ctx.doc) return;

    const sel = this.textToSelection(ctx.doc.width, ctx.doc.height, text, p.x, p.y);
    if (sel && !sel.isEmpty()) {
      ctx.emit(new SetSelectionCommand(sel));
    }
  }

  private textToSelection(docW: number, docH: number, text: string, x: number, y: number): Selection | null {
    const fontCss = `${this.fontStyle} ${this.fontWeight} ${this.fontSize}px "${this.fontFamily}"`;
    const rasterize = this.textRasterizer ?? noopTextRasterizer;
    const mask = rasterize(docW, docH, text, x, y, fontCss, false, 0);
    return Selection.fromMask(docW, docH, mask);
  }
}
